import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { type Broker } from 'components/brokers/types'
import BrokerContext from 'components/context/BrokerContext'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import { Button } from 'slab'
import { type AdminEmployer, type GetAdminEmployersParams } from 'utils/web_api.schemas'

import BulkUpdateEmployerReviewsDialog from './BulkUpdateEmployerReviewsDialog'
import EmployerReviewFilters, {
  type EmployerFilterGroupType,
  filterConfigurations,
} from './EmployerReviewFilters'
import EmployerReviewsQuickFilters from './EmployerReviewsQuickFilters'
import EmployerReviewsTable, {
  type EmployerFilters,
  MAX_NUMBER_OF_SELECTED_ITEMS,
} from './EmployerReviewsTable'
import SyncFiltersWithURL from './SyncFiltersWithUrl'

type EmployerReviewsViewProps = {
  currentBroker?: Broker
}

const EmployerReviewsView = ({ currentBroker }: EmployerReviewsViewProps = {}) => {
  const [filters, setFilters] = useState<EmployerFilters>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'created_at', desc: true },
  ])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false)

  // Reset row selection when filters and/or sorting change. This is to prevent the user from bulk updating items no
  // longer visible on the page.
  useEffect(() => {
    setRowSelection({})
  }, [pagination.pageSize, sorting, JSON.stringify(filters)])

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Get selected item IDs from rowSelection state
  const selectedIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id] === true
  )

  // Update selectedItemIds when rowSelection changes
  useEffect(() => {
    setSelectedItemIds(selectedIds)
  }, [rowSelection])

  return (
    <BrokerContext.Provider value={{ currentBroker }}>
      <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2000px] mx-auto'>
        <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
          <div className='text-2xl font-semibold w-96'>Employer Reviews</div>
        </div>
        <SyncFiltersWithURL<
          EmployerFilters,
          AdminEmployer,
          EmployerFilterGroupType
        >
          filters={filters}
          pagination={pagination}
          setFilters={setFilters}
          defaultFilters={{ review_statuses: ['pending'] }}
          sortableKeys={['created_at', 'review_status']}
          filterConfigurations={filterConfigurations}
        />
        <EmployerReviewsQuickFilters
          filters={filters}
          setFilters={setFilters}
        />
        <EmployerReviewFilters
          filters={filters}
          setFilters={setFilters}
          pagination={pagination}
          setPagination={setPagination}
        />
        <div className='h-10 mb-2 flex items-center justify-between w-full'>
          <RowSelectionCountDisplay
            numberOfSelectedItems={numberOfSelectedItems}
            setRowSelection={setRowSelection}
            maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
          />
          <Button
            disabled={numberOfSelectedItems === 0}
            onClick={() => setIsBulkUpdateDialogOpen(true)}
            variant='primary'
          >
            Update Selected
          </Button>
        </div>
        <EmployerReviewsTable
          filters={filters}
          setFilters={setFilters}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
        <BulkUpdateEmployerReviewsDialog
          isOpen={isBulkUpdateDialogOpen}
          setIsOpen={setIsBulkUpdateDialogOpen}
          selectedItems={selectedItemIds}
          onSuccess={() => setRowSelection({})}
          params={{
            'page[number]': pagination.pageIndex,
            'page[size]': pagination.pageSize,
            ...(filters.name ? { 'filter[name]': filters.name } : {}),
            ...(filters.review_statuses
              ? { 'filter[review_statuses][]': filters.review_statuses as any }
              : {}),
            ...(filters.employer_ids
              ? { 'filter[employer_ids][]': filters.employer_ids }
              : {}),
            ...(filters.bank_partners
              ? { 'filter[bank_partners][]': filters.bank_partners as any }
              : {}),
            ...(filters.bank_onboarding_status
              ? { 'filter[bank_onboarding_status]': filters.bank_onboarding_status as any }
              : {}),
            ...(filters.onboarding_status
              ? { 'filter[onboarding_status][]': filters.onboarding_status as any }
              : {}),
            ...(filters.invited_employees
              ? { 'filter[invited_employees]': filters.invited_employees === 'true' }
              : {}),
            ...(filters.has_confirmed_enrollments
              ? {
                  'filter[has_confirmed_enrollments]':
                    filters.has_confirmed_enrollments === 'true',
                }
              : {}),
            ...(filters.broker_ids
              ? { 'filter[broker_ids][]': filters.broker_ids }
              : {}),
            ...(sorting.length > 0
              ? {
                  sort: `${sorting[0].desc ? '-' : '+'}${sorting[0].id}`,
                }
              : {}),
          } as GetAdminEmployersParams}
        />
      </div>
    </BrokerContext.Provider>
  )
}

export default EmployerReviewsView
